import { Description, ErrorMessage, FieldError, Label, TextArea, TextField } from "@heroui/react";
import type { ReactNode } from "react";
import { type Control, Controller, type FieldPath, type FieldValues } from "react-hook-form";
import { clsx } from "@/src/common/utils/clsx";

interface TextareaGroupProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> {
	control: Control<TFieldValues>;
	name: TName;
	label?: ReactNode;
	help?: ReactNode;
	placeholder?: string;
	rows?: number;
	isRequired?: boolean;
	className?: string;
}

export function TextareaGroup<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
	control,
	name,
	label,
	help,
	placeholder,
	rows = 4,
	isRequired,
	className,
}: TextareaGroupProps<TFieldValues, TName>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<TextField
					className={clsx("flex w-full flex-col gap-1.5", className)}
					isInvalid={Boolean(fieldState.error)}
					isRequired={isRequired}
					value={(field.value as string | undefined) ?? ""}
					onChange={field.onChange}
					onBlur={field.onBlur}
					name={field.name}
				>
					{label ? <Label>{label}</Label> : null}
					<TextArea ref={field.ref} placeholder={placeholder} rows={rows} />
					{help ? <Description>{help}</Description> : null}
					<FieldError>
						<ErrorMessage>{fieldState.error?.message}</ErrorMessage>
					</FieldError>
				</TextField>
			)}
		/>
	);
}
